import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import MuiTooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import SVG from "@/assets/svg";
import CustomAvatar from "@/component/avatar";
import CostumeButton from "@/component/button";
import { CustomCard } from "@/component/card";
import DashboardChart from "@/component/chart/dashboardpieChart";
import DashboardListItem from "@/component/dashboard-list-item";
import DashboardScrollRegion from "@/component/dashboard-scroll-region";
import Layout from "@/component/layout";
import ListView from "@/component/list";
// import { IMAGES } from "@/assets/images";
import DefaultLoader from "@/component/loader";
import DotLoader from "@/component/loader/DotLoader";
import { Staff } from "@/interfaces/IAuth";
import { TodayIncident } from "@/interfaces/IDailyNotes";
import { TodayMedication } from "@/interfaces/IServiceUser";
import { TaskStatus } from "@/interfaces/ITaskManagement";
import { useAppSelector } from "@/redux/hooks";
import { $public } from "@/services";
import { getTodayIncidents } from "@/services/daily-notes";
import { getDashboardData } from "@/services/dashboard";
import { getTodayVisitsCount } from "@/services/roster";
import { getChartData } from "@/services/service-users";
import { getTodayMedications } from "@/services/service-users/medication";
import { getStaff } from "@/services/staff";
import { getTasksStatusCount } from "@/services/task-management";
import { ACCESS_LEVEL, GRAPH_DATA } from "@/utils/constants";
import { numberShortener } from "@/utils/helpers";
import logger from "@/runtime-utils/observability/logger";
import DashboardStyled from "./dashboardStyled";

const COUNTER_META = [
  {
    id: "dailyNotesCount",
    option: "Daily Notes",
    children: <SVG.DailyNote width="31px" />,
    href: "/dailyNote",
  },
  {
    id: "handoverCount",
    option: "Pinned Handovers",
    children: <SVG.Document width="31px" />,
    href: "/handover",
  },
  {
    id: "chartsCount",
    option: "Total Charts",
    children: <SVG.Doc width="30px" />,
    href: "/service/charts",
  },
  {
    id: "documentsOverdue",
    option: "Support plan overdue",
    children: <SVG.Care width="30px" />,
    href: "/service/document",
  },
  {
    id: "administeredMedication",
    option: "Administered Medication",
    children: <SVG.People width="31px" />,
    href: "/service/medication",
  },
  {
    id: "incidentsCount",
    option: "Reported Incidents",
    children: <SVG.Incident width="30px" />,
    href: "/dailyNote",
  },
  {
    id: "completedTasks",
    option: "Completed tasks",
    children: <SVG.DailyNote width="30px" />,
    href: "/tasks-management",
  },
  {
    id: "todayVisits",
    option: "Scheduled visits",
    children: <SVG.Roster width="31px" />,
    href: "/service/schedule-visit",
  },
] as const;

const dashboardCounterIconSx = {
  borderRadius: "4px",
  boxShadow: "0 0 7px rgb(204 204 204)",
  height: "36px",
  marginBottom: "5px",
  width: "36px",
  "& > svg": {
    display: "block",
    height: "22px !important",
    margin: "0 !important",
    width: "22px !important",
  },
} as const;

const TASK_STATUS_META: {
  id: TaskStatus;
  title: string;
  icon: React.ReactNode;
}[] = [
  {
    id: "overdue",
    title: "Overdue",
    icon: <SVG.Timer width="41px" />,
  },
  {
    id: "pending",
    title: "Pending",
    icon: <SVG.Pending width="41px" />,
  },
  {
    id: "skipped",
    title: "Skipped",
    icon: <SVG.Play width="41px" />,
  },
  {
    id: "not needed",
    title: "Not Needed",
    icon: <SVG.Play width="41px" />,
  },
  {
    id: "completed",
    title: "Complete",
    icon: <SVG.Complete width="41px" />,
  },
];

const VISITS_META = [
  {
    id: "total",
    title: "All Visit",
  },
  {
    id: "completed",
    title: "Completed",
  },
  {
    id: "scheduled",
    title: "Scheduled",
  },
  {
    id: "in-progress",
    title: "In Progress",
  },
  {
    id: "overdue",
    title: "Overdue",
  },
  {
    id: "not-allocated",
    title: "Not allocated",
  },
];

function LockedFeatureSection({
  tooltip,
  children,
}: {
  tooltip: string;
  children: React.ReactNode;
}) {
  return (
    <MuiTooltip
      arrow
      placement="top"
      title={tooltip}
      slotProps={{
        tooltip: {
          sx: {
            boxShadow: "1px 2px 3px rgba(0,0,0,0.2)",
            fontSize: "12px",
            maxWidth: 320,
            zIndex: 9999,
          },
        },
      }}
    >
      <Box className="blurBox">{children}</Box>
    </MuiTooltip>
  );
}

function DashboardListLoader() {
  return (
    <Box className="dashboard-panel-loader">
      <CircularProgress />
    </Box>
  );
}

type Counter = Record<(typeof COUNTER_META)[number]["id"], number>;
type TaskStatusCounter = Record<TaskStatus, number>;
type VisitCounter = Record<(typeof VISITS_META)[number]["id"], number>;

function Dashboard() {
  const { tenantSubscription, tenantSubscriptionLoaded } = useAppSelector(
    (state) => state.terms
  );

  const { user } = useAppSelector((state) => state.user);

  const allowedPages = tenantSubscription?.currentSub?.plan?.allowedPages || [];

  const [incidents, setIncidents] = useState<TodayIncident[]>([]);
  const [incidentsLoading, setIncidentsLoading] = useState(true);
  const [visits, setVisits] = useState<VisitCounter>();
  const [visitsLoading, setVisitsLoading] = useState(true);
  const [counters, setCounters] = useState<Counter>();
  const [countersLoading, setCountersLoading] = useState(true);
  const [chartData, setChartData] = useState(GRAPH_DATA);
  const [chartLoader, setChartLoader] = useState(true);
  const [team, setTeam] = useState<Staff[]>([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const [taskCounter, setTaskCounter] = useState<TaskStatusCounter>();
  const [taskCounterLoading, setTaskCounterLoading] = useState(true);

  const [medicationCounts, setMedicationCounts] = useState<TodayMedication[]>(
    []
  );
  const [medicationLoading, setMedicationLoading] = useState(true);

  const fetchDashboardCounters = async () => {
    try {
      const response = await getDashboardData();

      if (response.success) {
        setCounters(response.data);
      }
    } catch (error) {
      logger.error("Error fetching dashboard counters", error);
    } finally {
      setCountersLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const res = await getChartData();
      if (res.success) {
        setChartData({
          ...GRAPH_DATA,
          labels: res?.data?.labels || [],
          series: res?.data?.series || [],
        });
      }
    } catch (error) {
      logger.error("Error fetching chart data", error);
    } finally {
      setChartLoader(false);
    }
  };

  const fetchTeam = async () => {
    try {
      const response = await getStaff({
        limit: 6,
      });

      if (response.success) {
        setTeam(response.data);
      }
    } catch (error) {
      logger.error("Error fetching staff", error);
    } finally {
      setTeamLoading(false);
    }
  };

  const fetchTaskStatus = async () => {
    try {
      const response = await getTasksStatusCount();

      if (response.success) {
        setTaskCounter(response.data);
      }
    } catch (error) {
      logger.error("Error fetching task status", error);
    } finally {
      setTaskCounterLoading(false);
    }
  };

  const fetchTodayMedications = async () => {
    try {
      const response = await getTodayMedications();

      if (response.success) {
        setMedicationCounts(response.data?.slice(0, 6) || []);
      }
    } catch (error) {
      logger.error("Error fetching today's medications", error);
    } finally {
      setMedicationLoading(false);
    }
  };

  const fetchTodayIncidents = async () => {
    try {
      const response = await getTodayIncidents();

      if (response.success) {
        const data = response.data as TodayIncident[];
        setIncidents(data);
        const incidentsCount = data.reduce((acc, { count }) => acc + count, 0);
        setCounters((prev) => ({
          ...prev,
          incidentsCount,
        }));
      }
    } catch (error) {
      logger.error("Error fetching today's incidents", error);
    } finally {
      setIncidentsLoading(false);
    }
  };

  const fetchTodayVisits = async () => {
    try {
      const response = await getTodayVisitsCount();

      if (response.success) {
        setVisits(response.data);
        setCounters((prev) => ({
          ...prev,
          todayVisits: response.data.total,
        }));
      }
    } catch (error) {
      logger.error("Error fetching dashboard counters", error);
    } finally {
      setVisitsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardCounters();
    fetchChartData();
    fetchTeam();
    fetchTaskStatus();
    fetchTodayMedications();
    fetchTodayIncidents();
    fetchTodayVisits();
  }, []);
  return (
    <>
      <Layout>
        <DashboardStyled>
          <Box className="dashboard-content">
            {!tenantSubscriptionLoaded && <DefaultLoader />}
            <Box className="JobCard">
              {tenantSubscriptionLoaded && (
                <Grid container spacing={2}>
                  {COUNTER_META.map((item, i) => {
                    let pathKey = item.href.slice(1);

                    let counter = (
                      <Box className="title">
                        {countersLoading ? (
                          <DotLoader />
                        ) : (
                          numberShortener(counters?.[item.id] ?? 0)
                        )}
                      </Box>
                    );

                    if (item.id === "todayVisits") {
                      pathKey = "roster";
                      counter = (
                        <Box className="title">
                          {visitsLoading ? (
                            <DotLoader />
                          ) : (
                            numberShortener(counters?.[item.id] ?? 0)
                          )}
                        </Box>
                      );
                    }

                    if (item.id === "incidentsCount") {
                      counter = (
                        <Box className="title">
                          {incidentsLoading ? (
                            <DotLoader />
                          ) : (
                            numberShortener(counters?.[item.id] ?? 0)
                          )}
                        </Box>
                      );
                    }

                    const isAllowed = allowedPages.includes(pathKey);

                    return (
                      <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
                        {isAllowed ? (
                          <Link
                            className="topBannerSection"
                            style={{ marginBottom: "20px" }}
                            href={item.href}
                          >
                            <CardContent className="monitoring">
                              <Box className="myCare">
                                <Box>
                                  <Typography
                                    className="dashboard-counter-icon"
                                    sx={dashboardCounterIconSx}
                                  >
                                    {item.children}
                                  </Typography>
                                  <Typography className="option">
                                    {item.option}
                                  </Typography>
                                </Box>
                                {counter}
                              </Box>
                            </CardContent>
                          </Link>
                        ) : (
                          <Box
                            className="topBannerSection"
                            style={{ marginBottom: "20px" }}
                          >
                            <CardContent className="monitoring">
                              <Box className="care">
                                <Box>
                                  <Typography
                                    className="dashboard-counter-icon"
                                    sx={dashboardCounterIconSx}
                                  >
                                    {item.children}
                                  </Typography>
                                  <Typography className="option">
                                    {item.option}
                                  </Typography>
                                </Box>
                                {counter}
                              </Box>
                            </CardContent>
                          </Box>
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
              )}
              {tenantSubscriptionLoaded && (
                <Grid
                  className="dashboard-main-panels"
                  container
                  spacing={2}
                  sx={{ mt: 2 }}
                >
                  {/* service user occupancy */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <DashboardChart
                        chartData={chartData}
                        loading={chartLoader}
                      />
                    </Box>
                  </Grid>
                  {/* team */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <CustomCard title="Team">
                        <Box className="team">
                          <DashboardScrollRegion ariaLabel="Today's team members">
                            <Grid container spacing={2}>
                              <ListView
                                data={team}
                                loading={teamLoading}
                                loader={<DashboardListLoader />}
                                renderItem={(item, i) => (
                                  <Grid key={i} size={{ xs: 12, md: 6 }}>
                                    <Link
                                      href={`/staff/staff-members/${encodeURIComponent(item._id)}`}
                                    >
                                      <DashboardListItem
                                        leading={
                                          <CustomAvatar
                                            src={item.avatar}
                                            name={item.name}
                                          />
                                        }
                                        label={item.name}
                                      />
                                    </Link>
                                  </Grid>
                                )}
                              />
                            </Grid>
                          </DashboardScrollRegion>
                          {[ACCESS_LEVEL.ADMINISTRATOR].includes(
                            user.accessLevel
                          ) ? (
                            <Box className="dashboard-panel-action">
                              <Link href="/staff/staff-members/add">
                                <CostumeButton className="addMember">
                                  <SVG.Plus />{" "}
                                  <span className="service-memeberlist-add-new">
                                    Create new team
                                  </span>
                                </CostumeButton>
                              </Link>
                            </Box>
                          ) : null}
                        </Box>
                      </CustomCard>
                    </Box>
                  </Grid>
                  {/* Incidents */}
                  <Grid sx={{ minWidth: 0 }} size={{ xs: 12, md: 6 }}>
                    {allowedPages.includes("dailyNote") ? (
                      <CustomCard title="Today's Incidents">
                        <div className="jobRole">
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: "16px",
                              fontWeight: "600",
                              padding: "0px 10px 10px",
                            }}
                          >
                            Check The Incidents Reported Today
                          </Typography>
                          <DashboardScrollRegion ariaLabel="Today's incident categories">
                            <Grid container spacing={2}>
                              <ListView
                                data={incidents}
                                loading={incidentsLoading}
                                loader={<DashboardListLoader />}
                                renderItem={(item) => (
                                  <Grid size={{ xs: 12, md: 6 }}>
                                    <Link
                                      href={`/dailyNote?task=${encodeURIComponent(item.title)}`}
                                    >
                                      <DashboardListItem
                                        leading={
                                          <Image
                                            src={$public(item.logo)}
                                            alt="icon"
                                            width={25}
                                            height={25}
                                          />
                                        }
                                        leadingSurface
                                        label={`${item.title} (${item.count})`}
                                      />
                                    </Link>
                                  </Grid>
                                )}
                              />
                            </Grid>
                          </DashboardScrollRegion>
                        </div>
                      </CustomCard>
                    ) : (
                      <LockedFeatureSection tooltip="Consider adding a Daily Notes feature and contact the Wisterias team for more information">
                        {/*<Image
                          src={IMAGES.UpgradeMD}
                          alt=""
                          style={{ height: "389px" }}
                        /> */}
                        <CustomCard title="Today's Incidents">
                          <div className="jobRole" style={{ height: "298px" }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontSize: "16px",
                                fontWeight: "600",
                                padding: "0px 10px 10px",
                              }}
                            >
                              Check The Incidents Reported Today
                            </Typography>
                            <Grid container spacing={2}>
                              <ListView
                                data={incidents}
                                loading={incidentsLoading}
                                loader={<DashboardListLoader />}
                                renderItem={(item) => (
                                  <Grid size={{ xs: 12, md: 6 }}>
                                    <Link
                                      href={`/dailyNote?task=${encodeURIComponent(item.title)}`}
                                    >
                                      <DashboardListItem
                                        leading={
                                          <Image
                                            src={$public(item.logo)}
                                            alt="icon"
                                            width={25}
                                            height={25}
                                          />
                                        }
                                        leadingSurface
                                        label={`${item.title} (${item.count})`}
                                      />
                                    </Link>
                                  </Grid>
                                )}
                              />
                            </Grid>
                          </div>
                        </CustomCard>
                      </LockedFeatureSection>
                    )}
                  </Grid>
                  {/* Tasks */}
                  <Grid sx={{ minWidth: 0 }} size={{ xs: 12, md: 6 }}>
                    {allowedPages.includes("tasks-management") ? (
                      <CustomCard title="Today's Tasks">
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: "16px",
                            fontWeight: "600",
                            padding: "8px 10px 10px",
                          }}
                        >
                          Check The Status Of Tasks Scheduled For Today
                        </Typography>
                        <Box className="jobRoleTask">
                          <DashboardScrollRegion ariaLabel="Today's task statuses">
                            <Grid container spacing={2}>
                              <ListView
                                data={TASK_STATUS_META}
                                loading={taskCounterLoading}
                                loader={<DashboardListLoader />}
                                renderItem={(item) => (
                                  <Grid size={{ xs: 12, md: 6 }}>
                                    <Link
                                      href={`/tasks-management?status=${encodeURIComponent(item.id)}`}
                                    >
                                      <DashboardListItem
                                        leading={item.icon}
                                        leadingSurface
                                        label={`${item.title} (${taskCounter?.[item.id] ?? 0})`}
                                      />
                                    </Link>
                                  </Grid>
                                )}
                              />
                            </Grid>
                          </DashboardScrollRegion>
                          <Box className="dashboard-panel-action">
                            <Link href="/tasks-management/task-add">
                              <CostumeButton className="addMember">
                                <SVG.Plus />{" "}
                                <span className="service-memeberlist-add-new">
                                  Create new task
                                </span>
                              </CostumeButton>
                            </Link>
                          </Box>
                        </Box>
                      </CustomCard>
                    ) : (
                      <LockedFeatureSection tooltip="Consider adding a Task Management feature and contact the Wisterias team for more information">
                        {/* <Image
                          src={IMAGES.UpgradeMD}
                          alt=""
                          style={{ height: "389px" }}
                        /> */}
                        <CustomCard title="Today's Tasks">
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: "16px",
                              fontWeight: "600",
                              boxShadow: "rgb(204, 204, 204) 0px 0px 7px",
                              padding: "8px 10px 10px",
                            }}
                          >
                            Check The Status Of Tasks Scheduled For Today
                          </Typography>
                          <Box className="jobRoleTask">
                            <DashboardScrollRegion ariaLabel="Today's task statuses">
                              <Grid container spacing={2}>
                                <ListView
                                  data={TASK_STATUS_META}
                                  loading={taskCounterLoading}
                                  loader={<DashboardListLoader />}
                                  renderItem={(item) => (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                      <Link
                                        href={`/tasks-management?status=${encodeURIComponent(item.id)}`}
                                      >
                                        <DashboardListItem
                                          leading={item.icon}
                                          leadingSurface
                                          label={`${item.title} (${taskCounter?.[item.id] ?? 0})`}
                                        />
                                      </Link>
                                    </Grid>
                                  )}
                                />
                              </Grid>
                            </DashboardScrollRegion>
                            <Box className="dashboard-panel-action">
                              <Link href="/tasks-management/task-add">
                                <CostumeButton className="addMember">
                                  <SVG.Plus /> Create new task
                                </CostumeButton>
                              </Link>
                            </Box>
                          </Box>
                        </CustomCard>
                      </LockedFeatureSection>
                    )}
                  </Grid>
                  {/* Medication */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    {allowedPages.includes("service/medication") ? (
                      <CustomCard title="Medication">
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: "16px",
                            fontWeight: "600",
                            padding: "8px 10px 10px",
                          }}
                        >
                          Check The Status Of Medication For Today
                        </Typography>
                        <div className="jobRoleMedication">
                          <DashboardScrollRegion ariaLabel="Today's medication statuses">
                            <Grid container spacing={2}>
                              <ListView
                                data={medicationCounts}
                                loading={medicationLoading}
                                loader={<DashboardListLoader />}
                                renderItem={(item) => (
                                  <Grid size={{ xs: 12, md: 6 }}>
                                    <Link
                                      href={`/service/medication/${encodeURIComponent(item.user?._id || "")}`}
                                    >
                                      <DashboardListItem
                                        leading={
                                          <CustomAvatar
                                            src={item.user?.avatar}
                                            name={
                                              item.user?.name || "Service user"
                                            }
                                          />
                                        }
                                        label={item.user?.name}
                                        trailing={
                                          <Box className="medicationList">
                                            <SVG.Medication width="20px" />{" "}
                                            {item.logs}/{item.medications}
                                          </Box>
                                        }
                                      />
                                    </Link>
                                  </Grid>
                                )}
                              />
                            </Grid>
                          </DashboardScrollRegion>
                        </div>
                      </CustomCard>
                    ) : (
                      <LockedFeatureSection tooltip="Consider adding a Medication feature and contact the Wisterias team for more information">
                        {/* <Image
                          src={IMAGES.UpgradeMD}
                          alt=""
                          style={{ height: "389px" }}
                        /> */}
                        <CustomCard title="Medication">
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: "16px",
                              fontWeight: "600",
                              padding: "8px 10px 10px",
                            }}
                          >
                            Check The Status Of Medication For Today
                          </Typography>
                          <div className="jobRoleMedication">
                            <DashboardScrollRegion ariaLabel="Today's medication statuses">
                              <Grid container spacing={2}>
                                <ListView
                                  data={medicationCounts}
                                  loading={medicationLoading}
                                  loader={<DashboardListLoader />}
                                  renderItem={(item) => (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                      <Link
                                        href={`/service/medication/${encodeURIComponent(item.user?._id || "")}`}
                                      >
                                        <DashboardListItem
                                          leading={
                                            <CustomAvatar
                                              src={item.user?.avatar}
                                              name={
                                                item.user?.name ||
                                                "Service user"
                                              }
                                            />
                                          }
                                          label={item.user?.name}
                                          trailing={
                                            <Box className="medicationList">
                                              <SVG.Medication width="20px" />{" "}
                                              {item.logs}/{item.medications}
                                            </Box>
                                          }
                                        />
                                      </Link>
                                    </Grid>
                                  )}
                                />
                              </Grid>
                            </DashboardScrollRegion>
                          </div>
                        </CustomCard>
                      </LockedFeatureSection>
                    )}
                  </Grid>
                  {/* Visits */}
                  <Grid
                    className="dashboard-visits-panel"
                    size={{ xs: 12, md: 6 }}
                  >
                    {allowedPages.includes("roster") ? (
                      <Box sx={{ minWidth: 0 }}>
                        <CustomCard title="Visits">
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: "16px",
                              fontWeight: "600",
                              padding: "8px 10px 10px",
                              textTransform: "capitalize",
                            }}
                          >
                            Check The Status Of Visit For Today
                          </Typography>
                          <div className="jobRoleMedication">
                            <DashboardScrollRegion ariaLabel="Today's visit statuses">
                              <Grid container spacing={2}>
                                <ListView
                                  data={VISITS_META}
                                  loading={visitsLoading}
                                  loader={<DashboardListLoader />}
                                  renderItem={(item) => (
                                    <Grid
                                      key={item.id}
                                      size={{ xs: 12, md: 6 }}
                                    >
                                      <Link href={"/roster"}>
                                        <DashboardListItem
                                          leading={<SVG.Desktop width="40px" />}
                                          leadingSurface
                                          label={`${item.title} (${visits?.[item.id] ?? 0})`}
                                        />
                                      </Link>
                                    </Grid>
                                  )}
                                />
                              </Grid>
                            </DashboardScrollRegion>
                          </div>
                        </CustomCard>
                      </Box>
                    ) : (
                      <LockedFeatureSection tooltip="Consider adding a Roster feature and contact the Wisterias team for more information">
                        <CustomCard title="Visits">
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: "16px",
                              fontWeight: "600",
                              padding: "8px 10px 10px",
                              textTransform: "capitalize",
                            }}
                          >
                            Check The Status Of Visit For Today
                          </Typography>
                          <div className="jobRoleMedication">
                            <DashboardScrollRegion ariaLabel="Today's visit statuses">
                              <Grid container spacing={2}>
                                <ListView
                                  data={VISITS_META}
                                  loading={visitsLoading}
                                  loader={<DashboardListLoader />}
                                  renderItem={(item) => (
                                    <Grid
                                      key={item.id}
                                      size={{ xs: 12, md: 6 }}
                                    >
                                      <Link href={"/roster"}>
                                        <DashboardListItem
                                          leading={<SVG.Desktop width="40px" />}
                                          leadingSurface
                                          label={`${item.title} (${visits?.[item.id] ?? 0})`}
                                        />
                                      </Link>
                                    </Grid>
                                  )}
                                />
                              </Grid>
                            </DashboardScrollRegion>
                          </div>
                        </CustomCard>
                      </LockedFeatureSection>
                    )}
                  </Grid>
                </Grid>
              )}
            </Box>
          </Box>
        </DashboardStyled>
      </Layout>
    </>
  );
}

export default Dashboard;
